import moment from 'moment-timezone';

import {
  ACTIVITY_FORMS_NUMBERS,
  ACTIVITY_FORMS_ORDER
} from 'pages/create-activity/constants';

import {
  getAddOns,
  getIntegrations,
  getPreparedPricesCurrency,
  getActivtyFuturePrices
} from 'utils/accommodations-and-activities/prepareAccommodationAndActivityData';

export const getPreparedDataToCreateActivity = (
  forms,
  scheduleActiveSubTab,
  pricesActiveSubTab,
  languages,
  isUpdate,
  deletedAddOns,
  deletedRelatedAccommodations,
  deletedIntegrations,
  isCopy
) => {
  const languagesLength = languages.length;
  let secondLanguage = false;
  if (languagesLength > 1) secondLanguage = languages[languagesLength - 1];

  const generalForm = _getGeneralForm(forms);
  const descriptionForm = { ...forms['descriptionForm'].values };
  const integrationForm = { ...forms['integrationForm'].values };
  const addOnsForm = { ...forms['addOnsForm'].values };
  const relatedForm = _getRelatedForm(forms);
  const activityFuturePricesForm = { ...forms['activityFuturePricesForm'].values };

  const photoObj = generalForm.coverPhoto.file
    ? { photo: generalForm.coverPhoto.file }
    : {};

  const ownerIdObj = {
    businessOwnerId:
      forms.businessOwner.selectedBusinessOwner.ownerId ||
      forms.businessOwner.selectedBusinessOwner.id
  };

  delete generalForm.coverPhoto;
  delete generalForm.activityType;

  const scheduleFormName =
    ACTIVITY_FORMS_ORDER[ACTIVITY_FORMS_NUMBERS.scheduleForm][
      scheduleActiveSubTab
    ];
  const scheduleForm = { ...forms[scheduleFormName].values };
  let scheduleDetails = _getScheduleDetails(scheduleForm);

  let pricesActiveSubTabIsOpen = pricesActiveSubTab;
  if (forms['generalForm'].values.activityType === 'Open') {
    pricesActiveSubTabIsOpen++;
  }
  const pricesFormName =
    ACTIVITY_FORMS_ORDER[ACTIVITY_FORMS_NUMBERS.pricesForm][
      scheduleActiveSubTab
    ][pricesActiveSubTabIsOpen];
  const priceForm = { ...forms[pricesFormName].values };
  let prices = _getPreparedPrices(priceForm, scheduleDetails);
  let futurePrices = getActivtyFuturePrices(isUpdate, activityFuturePricesForm);

  const hasFuturePrices = futurePrices.length > 0;
  let addOns = getAddOns(isUpdate, addOnsForm, secondLanguage);
  let integrations = getIntegrations(isUpdate, integrationForm);

  let deletedAddOnsObj = deletedAddOns.length
    ? {
        deletedAddOns: deletedAddOns
      }
    : {};
  let deletedRelatedAccommodationsObj = deletedRelatedAccommodations.length
    ? { deletedRelatedAccommodation: deletedRelatedAccommodations }
    : {};
  let deletedPlatformsObj = deletedIntegrations.length
    ? { deletedPlatforms: deletedIntegrations }
    : {};

  const deactivationPeriods = generalForm.deactivationPeriods?.length
    ? JSON.parse(JSON.stringify(generalForm.deactivationPeriods)).filter(({ startPeriod, endPeriod }) =>
      !!startPeriod && !!endPeriod)
    : [];

  delete generalForm.deactivationPeriods;

  if (isCopy) {
    delete generalForm.id;
    generalForm.productCode = '';
    addOns = addOns.map(addOn => ({ ...addOn, id: null }));
    integrations = integrations.map(integration => ({
      ...integration,
      id: null
    }));
    deletedAddOnsObj = {};
    deletedRelatedAccommodationsObj = {};
    deletedPlatformsObj = {};
  }

  return {
    ...photoObj,
    ...ownerIdObj,
    activity: {
      scheduleType: scheduleFormName,
      pricesType: pricesFormName,
      futurePriceForm: hasFuturePrices ? 'activityFuturePricesForm' : null,
      ...generalForm,
      ..._getGeneralInfoSecondLanguage(forms, secondLanguage),

      ...descriptionForm,
      ..._getDescriptionInfoSecondLanguage(forms, secondLanguage),

      schedule: {
        fromDate: moment(scheduleForm.availability?.startDate).format(
          'YYYY-MM-DD'
        ),
        toDate: scheduleForm.availability?.endDate
          ? moment(scheduleForm.availability?.endDate).format('YYYY-MM-DD')
          : null,
        cutoffPeriod: parseInt(scheduleForm.cutoffPeriod),
        cutoffPeriodTimeType: scheduleForm.cutoffPeriodTimeType,
        scheduleDetails: scheduleDetails
      },

      prices: prices,
      futureRatePlanIds: futurePrices,
      activityOnPlatforms: integrations,
      ...relatedForm
    },
    addOns: addOns,
    ...deletedAddOnsObj,
    ...deletedRelatedAccommodationsObj,
    ...deletedPlatformsObj,
    deactivationPeriods
  };
};

const _getGeneralForm = forms => {
  const { deactivatedDates, ...generalFormValues } = {
    ...forms['generalForm'].values
  };

  return {
    ...generalFormValues,
    isOpen: generalFormValues.activityType === 'Open',
    isPrivate: generalFormValues.activityType !== 'Open',
    minParticipants: parseInt(generalFormValues.minParticipants),
    maxParticipants: parseInt(generalFormValues.maxParticipants),
    deactivationPeriods: deactivatedDates?.map(
      ({ startDate, endDate, id, isCanceled }) => ({
        id,
        startPeriod: startDate?.format('YYYY-MM-DD'),
        endPeriod: endDate?.format('YYYY-MM-DD'),
        isCanceled
      })
    ) ?? []
  };
};

const _getRelatedForm = forms => {
  return {
    relatedAccommodations: forms[
      'relatedAccommodationsForm'
    ].values.relatedAccommodations.map(item => parseInt(item.id))
  };
};

const _getScheduleDetails = scheduleForm => {
  let scheduleDetails = [];

  if (scheduleForm.days) {
    scheduleDetails.push({
      days: Object.keys(scheduleForm.days)
        .filter(field => scheduleForm.days[field])
        .map(item => item)
    });
  } else if (scheduleForm.daysArray) {
    scheduleDetails = scheduleForm.daysArray.map(item => ({
      days: Object.keys(item.days)
        .filter(field => item.days[field])
        .map(item => item),
      startTimes: item.startTimeAndDuration.map(item => ({
        ...item,
        duration: parseInt(item.duration),
        startTime: moment(item.startTime).format('HH:mm:ssZZ')
      }))
    }));
  }

  return scheduleDetails;
};

const _getPreparedPrices = (priceForm, scheduleDetails) => {
  if (priceForm.days && priceForm.days.length) {
    return priceForm.days.map(item => {
      let categoryPrices = [];

      if (item.hasOwnProperty('categories')) {
        item.categories.map(item => {
          categoryPrices = [
            ...categoryPrices,
            ...getPreparedPricesCurrency(item)
          ];
        });
        return {
          schedule: [
            {
              days: [item.day]
            }
          ],
          prices: categoryPrices
        };
      } else {
        return {
          schedule: [
            {
              days: item.days
            }
          ],
          prices: getPreparedPricesCurrency(item)
        };
      }
    });
  } else if (priceForm.daysArray) {
    return priceForm.daysArray.map(item => {
      let categoryPrices = [];
      item.categories.map(item => {
        categoryPrices = [
          ...categoryPrices,
          ...getPreparedPricesCurrency(item)
        ];
      });
      return {
        schedule: [
          {
            days: item.days
          }
        ],
        prices: categoryPrices
      };
    });
  } else if (priceForm.categories) {
    let schedule = scheduleDetails.map(item => ({
      days: item.days
    }));
    let categoryPrices = [];
    priceForm.categories.map(item => {
      categoryPrices = [...categoryPrices, ...getPreparedPricesCurrency(item)];
    });
    return [
      {
        schedule: schedule,
        prices: categoryPrices
      }
    ];
  } else {
    let days = [];
    scheduleDetails.map(item => {
      days.push(...item.days);
    });
    return [
      {
        schedule: [
          {
            days: days
          }
        ],
        prices: getPreparedPricesCurrency(priceForm)
      }
    ];
  }
};

const _getGeneralInfoSecondLanguage = (forms, secondLanguage) => {
  const countryCode = 'No';

  if (!secondLanguage) return { [`title${countryCode}`]: null };

  const generalFormSecondLanguage = {
    ...forms[`generalForm-${secondLanguage}`].values
  };

  return {
    [`title${countryCode}`]: generalFormSecondLanguage.title
  };
};

const _getDescriptionInfoSecondLanguage = (forms, secondLanguage) => {
  const countryCode = 'No';

  if (!secondLanguage) {
    return {
      [`overview${countryCode}`]: null,
      [`whatToBring${countryCode}`]: null,
      [`additionalInfo${countryCode}`]: null,
      [`cancellation${countryCode}`]: null,
      [`included${countryCode}`]: [],
      [`notIncluded${countryCode}`]: []
    };
  }

  const generalFormSecondLanguage = {
    ...forms[`descriptionForm-${secondLanguage}`].values
  };
  const newSecondLanguageObject = {};

  Object.keys(generalFormSecondLanguage).map(
    field =>
      (newSecondLanguageObject[`${field}${countryCode}`] =
        generalFormSecondLanguage[field])
  );

  return newSecondLanguageObject;
};
